import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { STAGES } from "@/config/worksheets";
import { Plus, FileText, ChevronRight, Trash2, Download, Search, X, ArrowUp, ArrowDown, ChevronsUpDown, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { exportCsv } from "@/lib/exportCsv";

export default function SubmissionsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [stage, setStage] = useState("Baseline");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState({ key: "updated_at", dir: "desc" });

  const load = () => api.get("/assessments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const showParticipant = user?.role === "doctor" || user?.role === "admin";

  const filtered = (items || []).filter((a) => {
    if (typeFilter !== "all" && a.stage !== typeFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (a.title || "").toLowerCase().includes(q) ||
      (a.participant_name || "").toLowerCase().includes(q)
    );
  });
  const sortVal = (a, key) => {
    switch (key) {
      case "title": return (a.title || "").toLowerCase();
      case "stage": return a.stage || "";
      case "participant_name": return (a.participant_name || "").toLowerCase();
      case "completed_count": return a.completed_count || 0;
      case "worksheet_count": return a.worksheet_count || 0;
      case "updated_at": return a.updated_at ? new Date(a.updated_at).getTime() : 0;
      default: return "";
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const create = async () => {
    try {
      const { data } = await api.post("/assessments", { title: title || `${stage} Assessment`, stage });
      setShowNew(false); setTitle("");
      nav(`/assessment/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportRows = () => {
    if (sorted.length === 0) { toast.error("Nothing to export"); return; }
    const rows = sorted.map((a, i) => ({
      num: i + 1,
      name: a.title,
      type: a.stage,
      completed: a.completed_count || 0,
      started: a.worksheet_count || 0,
      updated: a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "",
      participant: a.participant_name || "",
    }));
    const columns = [
      { label: "#", key: "num" },
      { label: "Name", key: "name" },
      { label: "Type", key: "type" },
      { label: "Completed", key: "completed" },
      { label: "Started", key: "started" },
      { label: "Updated", key: "updated" },
      ...(showParticipant ? [{ label: "Participant", key: "participant" }] : []),
    ];
    exportCsv(`My Submissions - ${new Date().toISOString().slice(0, 10)}`, columns, rows);
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this submission?")) return;
    try { await api.delete(`/assessments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-stone-900">{t("My Submissions")}</h1>
          <p className="text-sm text-stone-500">{t("Your 90-day assessment booklets. Progress auto-saves as you fill.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportRows} data-testid="export-submissions-btn"
            disabled={filtered.length === 0}
            className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40">
            <Download className="h-4 w-4" /> {t("Export")}
          </button>
          <button onClick={() => setShowNew(true)} data-testid="add-submission-btn"
            className="flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
            <Plus className="h-4 w-4" /> {t("New Assessment")}
          </button>
        </div>
      </div>

      {items && items.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="submissions-search"
              placeholder={showParticipant ? t("Search by name or participant…") : t("Search by name…")}
              className="w-full rounded-md border border-[#E5DFD3] bg-white py-2 pl-9 pr-9 text-sm focus:border-emerald-600 focus:outline-none" />
            {query && (
              <button onClick={() => setQuery("")} data-testid="clear-search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 hover:text-stone-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="submissions-type-filter"
            className="rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none">
            <option value="all">{t("All types")}</option>
            {STAGES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </select>
          <span className="text-xs text-stone-500">{filtered.length} {t("of")} {items.length}</span>
        </div>
      )}

      {items === null ? (
        <p className="text-sm text-stone-400">{t("Loading…")}</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#CAD2C5] bg-white p-12 text-center" data-testid="empty-state">
          <FileText className="mx-auto h-10 w-10 text-emerald-700/40" />
          <p className="mt-3 font-serif text-xl text-stone-700">{t("No assessments yet")}</p>
          <p className="mb-4 text-sm text-stone-500">{t("Start your first assessment booklet to begin.")}</p>
          <button onClick={() => setShowNew(true)} data-testid="add-first-btn"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
            <Plus className="h-4 w-4" /> {t("Add assessment")}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#CAD2C5] bg-white p-12 text-center" data-testid="no-matches">
          <Search className="mx-auto h-10 w-10 text-emerald-700/40" />
          <p className="mt-3 font-serif text-xl text-stone-700">{t("No matching assessments")}</p>
          <button onClick={() => { setQuery(""); setTypeFilter("all"); }} data-testid="clear-filters"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#E5DFD3] px-4 py-2 text-sm text-stone-600 hover:bg-stone-50">
            <X className="h-4 w-4" /> {t("Clear filters")}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#E5DFD3] bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-[#E8EFE9] text-left">
              <tr>
                <th className="w-12 px-4 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">#</th>
                <SortHeader label={t("Name")} k="title" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("Type")} k="stage" sort={sort} onSort={toggleSort} />
                {showParticipant && <SortHeader label={t("Participant")} k="participant_name" sort={sort} onSort={toggleSort} />}
                <SortHeader label={t("Completed")} k="completed_count" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("Started")} k="worksheet_count" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("Updated")} k="updated_at" sort={sort} onSort={toggleSort} />
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-stone-500 text-right">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a, i) => (
                <tr key={a.id} onClick={() => nav(`/assessment/${a.id}`)} data-testid={`submission-${a.id}`}
                  className="group cursor-pointer border-t border-[#EFE9DE] hover:bg-[#F6F8F5] transition">
                  <td className="px-4 py-2 text-stone-500 tabular-nums">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-stone-800">{a.title}</td>
                  <td className="px-4 py-2">
                    <span className="rounded-full bg-[#E8EFE9] px-2 py-0.5 text-xs font-medium text-emerald-800">{t(a.stage)}</span>
                  </td>
                  {showParticipant && <td className="px-4 py-2 text-stone-600">{a.participant_name || "—"}</td>}
                  <td className="px-4 py-2 text-stone-600 tabular-nums">{a.completed_count || 0}</td>
                  <td className="px-4 py-2 text-stone-600 tabular-nums">{a.worksheet_count || 0}</td>
                  <td className="px-4 py-2 text-stone-500">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      {showParticipant && (
                        <button onClick={(e) => { e.stopPropagation(); nav(`/assessment/${a.id}/clinical`); }}
                          data-testid={`clinical-${a.id}`} title={t("Clinical snapshot")}
                          className="rounded-md p-2 text-stone-400 hover:bg-emerald-50 hover:text-emerald-700">
                          <Stethoscope className="h-4 w-4" />
                        </button>
                      )}
                      {(user?.role === "participant" || user?.role === "user_manager" || user?.role === "admin") && (
                        <button onClick={(e) => remove(a.id, e)} data-testid={`delete-${a.id}`}
                          className="rounded-md p-2 text-stone-400 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRight className="h-5 w-5 text-stone-400 group-hover:text-emerald-700" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="new-modal">
          <div className="w-full max-w-md rounded-lg border border-[#E5DFD3] bg-white p-6 shadow-xl">
            <h3 className="font-serif text-xl font-semibold text-stone-800">{t("New Assessment")}</h3>
            <label className="mt-4 mb-1 block text-sm font-medium text-stone-700">{t("Title")}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. My Baseline Assessment"
              data-testid="new-title" className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none" />
            <label className="mt-4 mb-1 block text-sm font-medium text-stone-700">{t("Stage")}</label>
            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="new-stage"
              className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none">
              {STAGES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="rounded-md border border-[#E5DFD3] px-4 py-2 text-sm text-stone-600 hover:bg-stone-50">{t("Cancel")}</button>
              <button onClick={create} data-testid="new-create" className="rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900">{t("Create & open")}</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function SortHeader({ label, k, sort, onSort }) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">
      <button onClick={() => onSort(k)} data-testid={`sort-${k}`}
        className={`flex items-center gap-1 uppercase tracking-wider hover:text-emerald-800 ${active ? "text-emerald-800" : ""}`}>
        {label}
        <Icon className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}
